/**
 * AIVA-6639 — AI disclaimer copy. Every string the notice surfaces renders
 * lives here so wording changes are a one-file edit.
 *
 * Bump {@link AI_NOTICE_VERSION} whenever {@link AI_NOTICE_COPY.body} changes:
 * the version is part of the acknowledgement storage key, so a bump re-gates
 * every user once on their next pane load.
 *
 * AIVA-8663: `body` is now the fallback for when Studio sends no notice text. A
 * Studio-configured text gets its own derived version (see ./policy), which has
 * no meaning to a user, so the meta lines below only show "Notice v…" for the
 * built-in version.
 */
export const AI_NOTICE_VERSION = "1.0";

const isBuiltIn = (version: string): boolean => version === AI_NOTICE_VERSION;
const dayCount = (days: number): string => `${days} ${days === 1 ? "day" : "days"}`;

export const AI_NOTICE_COPY = {
  title: "How AiVA uses AI",
  subtitle: "Please read before you continue",
  body:
    "Please be advised that portions of the content, data, or outputs provided through this platform " +
    "may be generated, assisted, or compiled using artificial intelligence (AI) technologies. " +
    "AI-generated outputs may occasionally contain inaccuracies, omissions, or outdated facts. " +
    "Users are encouraged to independently verify any critical information, data points, or legal " +
    "or financial guidance before relying on them.",
  checkboxLabel: "I have read and understood this notice.",
  continueLabel: "Continue to AiVA",
  closeLabel: "Close",
  closeAriaLabel: "Close notice",
  footerLink: "AI Notice",
  moreSectionTitle: "AI and compliance",
  saveError: "We couldn't save your acknowledgement. Please try again.",
  gateMeta: (days: number, version: string = AI_NOTICE_VERSION): string =>
    isBuiltIn(version)
      ? `Notice v${version} · You'll be asked to confirm again in ${dayCount(days)}.`
      : `You'll be asked to confirm again in ${dayCount(days)}.`,
  acknowledgedLine: (when: string): string => `Acknowledged ${when}`,
  validUntilLine: (when: string, version: string = AI_NOTICE_VERSION): string =>
    isBuiltIn(version) ? `Notice v${version} · valid until ${when}` : `Valid until ${when}`,
  moreCardMeta: (when: string | null, version: string = AI_NOTICE_VERSION): string => {
    if (!isBuiltIn(version)) return when ? `Acknowledged ${when}` : "Not yet acknowledged";
    return when ? `Notice v${version} · acknowledged ${when}` : `Notice v${version}`;
  },
} as const;
